import React from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../app/StyleGuide';
import windowService from '../services/windowService';
import { Container } from './Layout';

export type Crumb = {
  name: string;
  route?: string;
};

const Div = styled.div`
  position: relative;
  width: 15px;
`;

const Triangle = styled.div`
  position: relative;
  &:before,
  &:after {
    width: 0;
    height: 0;
    border-top: 30px inset transparent;
    border-bottom: 30px inset transparent;
    position: absolute;
    content: "";
    top: -30px;
  }
  &:before {
    left: -1px;
    border-left: 15px solid #fff};
  }
`;

const Line = styled(Triangle)`
  z-index: -1;
  &:before {
    left: 0;
    border-left-color: ${props => props.color};
  }
`;

function Chevron() {
  return (
    <Div>
      <Triangle />
      <Line color="#dfdfdf" />
    </Div>
  );
}

const MobileCrumbContainer = styled.nav`
  text-transform: uppercase;
  font-weight: ${Type.Weight.semibold};
  letter-spacing: 0.05em;
  p {
    margin: 0;
  }
  .fa-chevron-left {
    font-size: ${Type.Scale._1};
    position: relative;
    color: ${Type.Color.dark};
    opacity: 0.5;
    margin-right: ${Grid._2};
  }
`;

export function MobileCrumb({ crumbs }: { crumbs: Crumb[] }) {
  const crumb = [...crumbs].reverse().find(c => c.route);

  if (!crumb) return null;

  return (
    <MobileCrumbContainer data-qa-hook="mobileBreadcrumb">
      <p>
        <span className="fa fa-chevron-left"></span>{' '}
        <a onClick={() => windowService.redirectTo(crumb.route)}>{crumb.name}</a>
      </p>
    </MobileCrumbContainer>
  );
}

const CrumbContainerFluid = styled.div`
  z-index: 100;
  position: relative;
  background: ${Color.white};
  box-shadow: inset 0px 1px 2px rgba(70, 72, 84, 0.1);
  border-bottom: 1px solid ${Color.Gray._10};
`;

const Crumbs = styled.nav`
  display: flex;
  align-items: center;
  margin: 0;
`;

const Crumb = styled.a`
  margin: 20px ${Grid._4};
  font-size: ${Type.Scale._2};
  text-transform: uppercase;
  font-weight: ${Type.Weight.semibold};
  letter-spacing: 0.05em;
  border-bottom: 2px solid transparent;
  transition: all 0.15s ease-in-out;
  &:hover {
    border-bottom-color: ${Color.Blue._50};
  }
  &:first-child {
    margin-left: 0;
  }
  @media screen and (min-width: ${Breakpoints.screen_lg}) {
    &:first-child {
      margin-left: ${Grid._4};
    }
  }
`;

const CurrentPage = styled.span`
  font-size: ${Type.Scale._2};
  margin-left: ${Grid._4};
`;

export function BreadcrumbsSubMenu({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <CrumbContainerFluid>
      <Container>
        <Crumbs>
          {crumbs.map((link, i) => (
            <React.Fragment key={link.name}>
              {link.route && (
                <Crumb
                  data-qa-hook="breadcrumb"
                  className="crumb"
                  href={link.route}
                  onClick={event => {
                    event.preventDefault();
                    windowService.redirectTo(link.route);
                  }}
                >
                  <span data-qa-hook="breadcrumbName">{link.name}</span>
                </Crumb>
              )}
              {i + 1 < crumbs.length && <Chevron />}
              {!link.route && <CurrentPage>{link.name}</CurrentPage>}
            </React.Fragment>
          ))}
        </Crumbs>
      </Container>
    </CrumbContainerFluid>
  );
}

export default function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <>
      <div className="hidden-xs hidden-sm">
        <BreadcrumbsSubMenu crumbs={crumbs} />
      </div>
      <Container className="visible-xs-block visible-sm-block" style={{ margin: `${Grid._4} 0` }}>
        <MobileCrumb crumbs={crumbs} />
      </Container>
    </>
  );
}
